import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { formatEur, formatDateShort, formatDuration, MISSION_TYPE_LABEL, type SpecialtyInfo } from '@/lib/format'
import { useSpecialtyConfig } from '@/lib/hooks/useSpecialtyConfig'

interface AvailableMission {
  id: string
  title: string
  description: string
  type: string
  city: string
  address: string
  date: string
  duration: number
  amount: number
  radiusKm: number
  distanceKm: number | null
  requiredSpecialty: string | null
  isTargeted?: boolean
  company: {
    id: string
    name: string
    logoUrl: string | null
  }
}

function MissionCard({ mission, badge }: { mission: AvailableMission; badge: (key: string) => SpecialtyInfo }) {
  const specialty = mission.requiredSpecialty ? badge(mission.requiredSpecialty) : null
  return (
    <Link to={`/missions-disponibles/${mission.id}`} className="glass-card-hover p-5 block group">
      <div className="flex items-start gap-4">
        {/* Logo entreprise */}
        <div className="w-10 h-10 rounded-xl bg-white/[0.08] border border-white/[0.12] flex items-center justify-center flex-shrink-0 text-text-secondary font-semibold text-sm">
          {mission.company.name[0].toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div>
              <p className="text-text-muted text-xs mb-0.5">{mission.company.name}</p>
              <h3 className="text-text-primary font-semibold text-sm leading-snug">{mission.title}</h3>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[#8B9AFF] font-bold text-lg leading-tight">{formatEur(mission.amount)}</p>
              <p className="text-text-muted text-xs">{formatDuration(mission.duration)}</p>
            </div>
          </div>

          <p className="text-text-muted text-xs leading-relaxed line-clamp-2 mb-3">
            {mission.description}
          </p>

          <div className="flex items-center gap-2 flex-wrap">
            {mission.isTargeted && (
              <span className="text-xs px-2 py-0.5 rounded-full border bg-warning/15 border-warning/30 text-warning font-medium" title="Cette entreprise vous a choisi — réservée pour vous 15 min">
                ⭐ On vous a choisi
              </span>
            )}
            <span className="text-xs px-2 py-0.5 rounded-full border bg-white/[0.06] border-white/[0.12] text-text-secondary">
              {MISSION_TYPE_LABEL[mission.type] ?? mission.type}
            </span>
            {specialty && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${specialty.badgeClass}`}>
                {specialty.icon} {specialty.shortLabel}
              </span>
            )}
            <span className="text-text-secondary text-xs flex items-center gap-1">
              <span>📅</span> {formatDateShort(mission.date)}
            </span>
            <span className="text-text-secondary text-xs flex items-center gap-1">
              <span>📍</span> {mission.city}
              {mission.distanceKm !== null && (
                <span className="text-success ml-1">({mission.distanceKm} km)</span>
              )}
            </span>
          </div>
        </div>

        <span className="text-text-muted text-sm group-hover:text-text-secondary transition-colors flex-shrink-0 mt-3">→</span>
      </div>
    </Link>
  )
}

export default function MissionsDisponiblesPage() {
  const [missions, setMissions] = useState<AvailableMission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { badge } = useSpecialtyConfig()

  useEffect(() => {
    apiFetch<AvailableMission[]>('/assignments/available')
      .then(setMissions)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Missions disponibles</h1>
        <p className="text-text-secondary text-sm mt-1">
          {loading ? '…' : `${missions.length} mission${missions.length !== 1 ? 's' : ''} à proximité`}
        </p>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="glass-card h-32 animate-pulse" />)}
        </div>
      ) : missions.length === 0 ? (
        <div className="glass-card p-14 text-center">
          <p className="text-5xl mb-5">📭</p>
          <p className="text-text-primary font-semibold text-lg mb-2">Aucune mission disponible</p>
          <p className="text-text-secondary text-sm max-w-sm mx-auto">
            Mettez à jour votre position dans votre profil pour voir les missions autour de vous.
          </p>
          <Link
            to="/profil-prestataire"
            className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block mt-6"
          >
            Mettre à jour ma position →
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {missions.map((m) => <MissionCard key={m.id} mission={m} badge={badge} />)}
        </div>
      )}
    </div>
  )
}
